import { useEffect, useState } from 'react';
import { X, Share, Plus, Home, Menu, Download } from 'lucide-react';

/**
 * InstallAppModal — platform-aware PWA install guide.
 *
 *  • iOS Safari      → step-by-step visual instructions (Share → Add to Home Screen)
 *  • Chrome / Edge   → fires deferredPrompt.prompt() when available; otherwise guide
 *  • Android browsers→ "tap ⋮ → Install app" hint
 *  • Desktop         → shows install icon location in the address bar
 *
 * Controlled component: parent passes `open` + `onClose` + optional `deferredPrompt`.
 */
function detectPlatform() {
  if (typeof window === 'undefined') return 'unknown';
  const ua = window.navigator.userAgent;

  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const isAndroid = /Android/.test(ua);
  const isCriOS = /CriOS|EdgiOS|FxiOS/.test(ua); // non-Safari browsers on iOS
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR|Brave/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isSamsung = /SamsungBrowser/.test(ua);

  if (isIOS && !isCriOS) return 'ios-safari';
  if (isIOS && isCriOS)  return 'ios-other';
  if (isAndroid)         return isSamsung ? 'android-samsung' : 'android-chrome';
  if (isChrome || isEdge) return 'desktop-chromium';
  return 'other';
}

export default function InstallAppModal({ open, onClose, deferredPrompt }) {
  const [platform, setPlatform] = useState('unknown');

  useEffect(() => {
    if (open) setPlatform(detectPlatform());
  }, [open]);

  if (!open) return null;

  const handleChromeInstall = async () => {
    if (!deferredPrompt?.prompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      onClose();
    } catch {
      // Ignore — user dismissed
    }
  };

  const canChromeInstallNow = platform === 'android-chrome' || platform === 'desktop-chromium';
  const hasDeferred = canChromeInstallNow && deferredPrompt?.prompt;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="install-app-modal"
    >
      {/* Backdrop */}
      <button
        aria-label="Close install guide"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      <div
        className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl border overflow-hidden animate-[slideUp_250ms_ease-out]"
        style={{ background: '#FDFBF7', borderColor: '#E6E2D8' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#E6E2D8' }}>
          <div className="flex items-center gap-3">
            <img src="/plexura-logo.png" alt="Plexura" className="w-10 h-10 object-contain" />
            <div>
              <p className="font-semibold text-[#1C1C19]" style={{ fontFamily: 'Manrope, sans-serif' }}>Install Plexura AI Scribe</p>
              <p className="text-xs text-[#8A8A80]">Launches full-screen, loads instantly</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="install-modal-close"
            aria-label="Close"
            className="p-1.5 rounded-md text-[#8A8A80] hover:bg-[#F3F0E6] hover:text-[#1C1C19] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — platform-specific */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {platform === 'ios-safari' && <IOSSafariSteps onDone={onClose} />}
          {platform === 'ios-other' && <IOSOtherBrowserHint onDone={onClose} />}
          {platform === 'android-samsung' && <AndroidSamsungSteps onDone={onClose} />}
          {platform === 'android-chrome' && (
            hasDeferred
              ? <AndroidChromeAutoInstall onInstall={handleChromeInstall} />
              : <AndroidChromeManualSteps onDone={onClose} />
          )}
          {platform === 'desktop-chromium' && (
            hasDeferred
              ? <DesktopAutoInstall onInstall={handleChromeInstall} />
              : <DesktopManualSteps onDone={onClose} />
          )}
          {(platform === 'other' || platform === 'unknown') && <OtherBrowserHint onDone={onClose} />}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(24px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ─── Platform-specific step panels ─── */

const StepsList = ({ steps }) => (
  <ol className="space-y-4" data-testid="install-steps-list">
    {steps.map((s, i) => (
      <li key={i} className="flex items-start gap-3">
        <div
          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
          style={{ background: '#2D3A33' }}
        >
          {i + 1}
        </div>
        <div className="flex-1 pt-0.5 text-sm text-[#1C1C19] leading-relaxed">{s}</div>
      </li>
    ))}
  </ol>
);

const DoneButton = ({ onClick, label = 'Got it' }) => (
  <button
    onClick={onClick}
    data-testid="install-modal-done"
    className="mt-5 w-full h-11 rounded-xl text-sm font-semibold transition-colors"
    style={{ background: '#2D3A33', color: '#FDFBF7' }}
  >
    {label}
  </button>
);

function IOSSafariSteps({ onDone }) {
  return (
    <>
      <p className="text-xs text-[#5C5C54] mb-4">
        On iPhone & iPad, Safari doesn't show an auto-install prompt. Follow these 3 taps:
      </p>
      <StepsList
        steps={[
          <>Tap the <strong>Share</strong> icon <Share className="inline w-4 h-4 mx-1 -mt-0.5" style={{ color: '#C47A5E' }} /> at the bottom of Safari.</>,
          <>In the share sheet, scroll down and tap <strong>Add to Home Screen</strong> <Plus className="inline w-4 h-4 mx-1 -mt-0.5" style={{ color: '#C47A5E' }} />.</>,
          <>Tap <strong>Add</strong> in the top-right. Plexura AI Scribe appears on your home screen — tap the icon to launch.</>,
        ]}
      />
      <div className="mt-5 p-3 rounded-xl text-xs text-[#5C5C54] border" style={{ background: '#F3F0E6', borderColor: '#E6E2D8' }}>
        <strong>Tip:</strong> the Share icon is at the <em>bottom centre</em> of Safari on iPhone and the <em>top right</em> on iPad.
      </div>
      <DoneButton onClick={onDone} />
    </>
  );
}

function IOSOtherBrowserHint({ onDone }) {
  return (
    <>
      <p className="text-sm text-[#1C1C19] mb-3">
        iOS only allows installing web apps from <strong>Safari</strong>.
      </p>
      <StepsList
        steps={[
          <>Copy the current URL and open it in <strong>Safari</strong> (it's the blue compass icon).</>,
          <>In Safari, tap the <strong>Share</strong> icon <Share className="inline w-4 h-4 mx-1 -mt-0.5" style={{ color: '#C47A5E' }} /> at the bottom.</>,
          <>Scroll and tap <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.</>,
        ]}
      />
      <DoneButton onClick={onDone} />
    </>
  );
}

function AndroidChromeAutoInstall({ onInstall }) {
  return (
    <>
      <p className="text-sm text-[#1C1C19] mb-4">
        Chrome can install Plexura directly. Tap the button below — it'll add the app icon to your home screen and launch it full-screen.
      </p>
      <button
        onClick={onInstall}
        data-testid="install-modal-chrome-cta"
        className="w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
        style={{ background: '#2D3A33', color: '#FDFBF7' }}
      >
        <Download className="w-4 h-4" />
        Install App to Home Screen
      </button>
    </>
  );
}

function AndroidChromeManualSteps({ onDone }) {
  return (
    <>
      <p className="text-xs text-[#5C5C54] mb-4">
        On Android Chrome, the install option is in the browser menu:
      </p>
      <StepsList
        steps={[
          <>Tap the <strong>menu</strong> icon <Menu className="inline w-4 h-4 mx-1 -mt-0.5" style={{ color: '#C47A5E' }} /> in the top-right of Chrome.</>,
          <>Tap <strong>Install app</strong> (or <strong>Add to Home screen</strong>).</>,
          <>Tap <strong>Install</strong>. The app icon appears on your home screen — tap it to launch.</>,
        ]}
      />
      <DoneButton onClick={onDone} />
    </>
  );
}

function AndroidSamsungSteps({ onDone }) {
  return (
    <>
      <p className="text-xs text-[#5C5C54] mb-4">Samsung Internet install path:</p>
      <StepsList
        steps={[
          <>Tap the <strong>menu</strong> icon <Menu className="inline w-4 h-4 mx-1 -mt-0.5" style={{ color: '#C47A5E' }} /> at the bottom-right.</>,
          <>Tap <strong>Add page to</strong> → <strong>Home screen</strong>.</>,
          <>Confirm by tapping <strong>Add</strong>.</>,
        ]}
      />
      <DoneButton onClick={onDone} />
    </>
  );
}

function DesktopAutoInstall({ onInstall }) {
  return (
    <>
      <p className="text-sm text-[#1C1C19] mb-4">
        Install Plexura as a desktop app — it'll run in its own window without browser chrome.
      </p>
      <button
        onClick={onInstall}
        data-testid="install-modal-desktop-cta"
        className="w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
        style={{ background: '#2D3A33', color: '#FDFBF7' }}
      >
        <Download className="w-4 h-4" />
        Install Desktop App
      </button>
    </>
  );
}

function DesktopManualSteps({ onDone }) {
  return (
    <>
      <p className="text-xs text-[#5C5C54] mb-4">
        Look for a small <strong>install icon</strong> on the right side of your browser's address bar (looks like a computer with a down-arrow). Click it, then click <strong>Install</strong>.
      </p>
      <p className="text-xs text-[#5C5C54]">
        If you don't see the icon, open the browser menu and look for "Install Plexura AI Scribe" or "Add to Home screen".
      </p>
      <DoneButton onClick={onDone} />
    </>
  );
}

function OtherBrowserHint({ onDone }) {
  return (
    <>
      <p className="text-sm text-[#1C1C19] mb-2">
        Your browser may not support PWA install yet.
      </p>
      <p className="text-xs text-[#5C5C54] mb-2">
        For the best experience, open this site in <strong>Safari (iPhone/iPad)</strong> or <strong>Chrome (Android/Desktop)</strong> and try installing from there.
      </p>
      <p className="text-xs text-[#5C5C54]">
        You can still bookmark the page and add it manually via <Home className="inline w-3.5 h-3.5 mx-0.5" /> your browser's options.
      </p>
      <DoneButton onClick={onDone} />
    </>
  );
}
